import { PaginationQueryObject, QueryConfig, SortDirection } from "./Queries";

const defaultPaginationInput = {
  pageNumber: 1,
  pageSize: 1000,
  sortField: "name",
  sortDirection: SortDirection.ASC,
};

export const getHierarchies = (
  filter: any,
  paginationInput: PaginationQueryObject = defaultPaginationInput
): QueryConfig => {
  const query = `query getHierarchies($filter: OrganizationalHierarchyFilter, $paginationInput: PaginationInput) {
            organizationalHierarchies (filter: $filter, paginationInput: $paginationInput) {
                items {
                    id
                    name
                    topOrgUnit {
                        id
                    }
                }
                pageInfo {
                    pageNumber
                    pageSize
                    totalPages
                    totalElements
                }
            }
        }`;

  return {
    query: query,
    variables: {
      filter,
      paginationInput,
    },
  };
};

export const getOrganizationalUnits = (filter: any): QueryConfig => {
  const query = `query getOrganizationalUnits($filter: OrganizationalUnitFilter, $paginationInput: PaginationInput) {
            organizationalUnits (filter: $filter, paginationInput: $paginationInput) {
                items {
                    id
                    label: name
                    hasChildren
                }
            }
        }`;

  const paginationInput = {
    pageSize: 1000,
    sortField: "name",
    sortDirection: SortDirection.ASC,
  };

  return {
    query: query,
    variables: {
      filter,
      paginationInput,
    },
  };
};

export const getOrganizationalUnitDetails = (id: string): QueryConfig => {
  const query = `query getOrgUnitDetails($id: String!, $filter: LocationFilter, $paginationInput: PaginationInput) {
            organizationalUnit(id: $id) {
                id
                name
                lineage
                ouCode
                locations(filter: $filter, paginationInput: $paginationInput) {
                  items {
                    id
                    name
                    lineage
                  }
                  pageInfo {
                    pageNumber, pageSize, totalPages, totalElements, hasNext
                  }
                }
                organizationalHierarchy {
                  id
                  name
                  topOrgUnit {
                    id
                  }
                }
                viewerCanAddChildOrgUnit
                viewerCanDeleteOrgUnit
                viewerCanManageOrgUnit
                viewerCanMoveOrgUnit
            }
        }`;
  return {
    query: query,
    variables: {
      id,
      filter: null,
      paginationInput: {
        sortDirection: SortDirection.ASC,
        sortField: "name",
      },
    },
  };
};

export const getOrgUnitLocations = (
  orgUnitId: string,
  filter: any,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getOrgUnitLocations($id: String!, $filter: LocationFilter, $paginationInput: PaginationInput) {
            organizationalUnit(id: $id) {
                locations(filter: $filter, paginationInput: $paginationInput) {
                  items {
                    id
                    name
                    lineage
                  }
                  pageInfo {
                    pageSize, pageNumber, totalElements, totalPages, hasNext, hasPrevious
                  }
                }
            }
        }`;

  return {
    query: query,
    variables: {
      id: orgUnitId,
      paginationInput: pagination,
      filter,
    },
  };
};

export const createOrganizationalHierarchy = (
  customerId: string,
  name: string
): QueryConfig => {
  const query = `mutation ($input: OrganizationalHierarchyCreateInput!) {
            createOrganizationalHierarchy(input: $input) {
                id
            }
        }`;
  return {
    query: query,
    variables: {
      input: {
        customerId,
        name,
      },
    },
  };
};

export const createOrganizationalUnit = (
  customerID: string,
  orgHierarchyID: string,
  parentOrgID: string,
  name: string
): QueryConfig => {
  const query = `mutation ($input: OrganizationalUnitCreateInput!) {
            createOrganizationalUnit(input: $input) {
                id
            }
        }`;
  return {
    query: query,
    variables: {
      input: {
        customerID,
        orgHierarchyID,
        parentOrgID,
        name,
      },
    },
  };
};

export const updateOrgUnit = (
  orgUnitId: string,
  name?: string,
  newParentId?: string
): QueryConfig => {
  const query = `mutation ($input: OrganizationalUnitUpdateInput!) {
    updateOrganizationalUnit(input: $input) {
        id
        name
        lineage
        ouCode
    }
}`;

  return {
    query,
    variables: {
      input: {
        id: orgUnitId,
        name,
        parentOrgID: newParentId,
      },
    },
  };
};

export const addLocationToOrg = (
  orgUnitId: string,
  locationId: string
): QueryConfig => {
  const query = `mutation ($input: OuLocationMapCreateInput!) {
    addLocationToOrganizationalUnit(input: $input) {
      orgUnit: ou {
        id
        name
      }
      location {
        id
        name
      }
    }
}`;

  return {
    query,
    variables: {
      input: {
        ouID: orgUnitId,
        locationID: locationId,
      },
    },
  };
};

export const removeLocationFromOrg = (
  orgUnitId: string,
  locationId: string
): QueryConfig => {
  const query = `mutation ($input: OuLocationMapDeleteInput!) {
    removeLocationFromOrganizationalUnit(input: $input)
  }`;

  return {
    query,
    variables: {
      input: {
        ouID: orgUnitId,
        locationID: locationId,
      },
    },
  };
};

export const updateHierarchy = (
  hierarchyId: string,
  name: string
): QueryConfig => {
  const query = `mutation ($input: OrganizationalHierarchyUpdateInput!) {
    updateOrganizationalHierarchy(input: $input) {
        id
        name
    }
}`;

  return {
    query,
    variables: {
      input: {
        id: hierarchyId,
        name,
      },
    },
  };
};

export const deleteOrgUnit = (orgUnitId: string): QueryConfig => {
  const query = `mutation ($id: String!) {
    deleteOrganizationalUnit(id: $id) 
}`;

  return {
    query,
    variables: {
      id: orgUnitId,
    },
  };
};

export const deleteHierarchy = (hierarchyId: string): QueryConfig => {
  const query = `mutation ($id: String!) {
    deleteOrganizationalHierarchy(id: $id) 
}`;

  return {
    query,
    variables: {
      id: hierarchyId,
    },
  };
};
